import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Tooltip, useTheme, Card, CardContent, Stack, TextField,
  Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Divider, Tab, Tabs
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon, ArrowForward as NextIcon,
  ArrowBack as BackIcon, Warning as WarnIcon, Error as ErrorIcon,
  CheckCircle as OkIcon, History as HistoryIcon,
  OpenInNew as OpenIcon, PlayArrow as ApplyIcon, Undo as RollbackIcon,
  Inventory as InvIcon, AccountBalance as AcctIcon, Receipt as JEIcon,
  Timeline as LifecycleIcon, Calculate as SimIcon, Verified as VerifiedIcon,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================
// Types
// ============================================================
interface DiagnosticIssue {
  issueType: string; issueTypeAr: string; severity: string;
  itemId?: number; itemCode?: string; itemName?: string; itemNameAr?: string;
  warehouseId?: number; branchId?: number;
  description: string; descriptionAr: string;
  suggestedFix?: string; suggestedFixAr?: string;
  canAutoCorrect: boolean;
  sourceType?: string; sourceId?: number; sourceDetailId?: number;
  currentValue?: number; expectedValue?: number; difference?: number;
  affectedDocuments: string[];
  // Root-cause trace (added 2026-05-18)
  causedByDocumentType?: string;
  causedByDocumentNumber?: string;
  causedByDocumentId?: number;
  causedByDocumentDate?: string;
  causedByUserId?: number;
  causedByUserName?: string;
  causedByCreatedAt?: string;
  rootCauseStory?: string;
  rootCauseStoryAr?: string;
  financialImpact?: number;
  causedByNavigationPath?: string;
  recommendedAction?: string;
  recommendedActionReason?: string;
  recommendedActionReasonAr?: string;
  causedByDocumentStatus?: string;
  causedByPostedFromPage?: string;
  causedByPostedByName?: string;
  causedByPostedAt?: string;
}
interface DiagnosticReport {
  scannedAt: string; costingMethod: string;
  totalItemsScanned: number; totalMovementsScanned: number; totalIssuesFound: number;
  criticalCount: number; warningCount: number; infoCount: number;
  issues: DiagnosticIssue[];
}
interface LifecycleEvent {
  eventOrder: number; eventDate: string; movementType: string; movementTypeAr: string;
  documentNumber: string; documentType: string; documentId?: number; documentDetailId?: number;
  navigationPath?: string; location?: string; unitCode?: string;
  qtyIn: number; qtyOut: number; unitCost: number; totalCost: number;
  runningBalance: number; runningValue: number;
  movementId?: number; journalEntryId?: number; journalEntryNumber?: string;
}
interface ItemLifecycle {
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  costingMethod: string;
  openingQuantity: number; totalIn: number; totalOut: number; currentRemaining: number;
  openingValue: number; currentValue: number;
  events: LifecycleEvent[];
}
interface MovementCmp { movementId: number; movementDate: string; sourceType: string; location?: string; quantity: number; remainingQuantity: number; oldUnitCost: number; newUnitCost: number; oldTotalCost: number; newTotalCost: number; delta: number; }
interface DetailCmp { entityType: string; detailId: number; parentId?: number; documentNumber: string; documentDate: string; documentType: string; navigationPath?: string; quantity: number; oldUnitCost: number; newUnitCost: number; oldTotalCost: number; newTotalCost: number; delta: number; }
interface JELineCmp { entryDetailId: number; lineNumber: number; accountId: number; accountCode?: string; accountName?: string; accountNameAr?: string; oldDebit: number; newDebit: number; oldCredit: number; newCredit: number; delta: number; }
interface JECmp { journalEntryId: number; entryNumber: string; entryDate: string; status: string; sourceType?: string; sourceDocumentId?: number; navigationPath?: string; oldTotalDebit: number; newTotalDebit: number; oldTotalCredit: number; newTotalCredit: number; delta: number; lines: JELineCmp[]; }
interface GLCmp { ledgerId: number; accountId: number; accountCode?: string; accountName?: string; accountNameAr?: string; transactionDate: string; journalEntryId: number; journalEntryNumber?: string; oldDebit: number; newDebit: number; oldCredit: number; newCredit: number; delta: number; }
interface ImpactedDoc { documentType: string; documentTypeAr: string; documentId: number; documentNumber: string; documentDate: string; status: string; navigationPath?: string; delta: number; }
interface SimWarning { severity: string; message: string; messageAr: string; }
interface SimulationResult {
  isValid: boolean; validationError?: string; validationErrorAr?: string;
  costingMethod: string;
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  oldTargetValue: number; newTargetValue: number;
  affectedMovementCount: number; affectedIssueDetailCount: number; affectedJournalEntryCount: number; affectedGLRowCount: number;
  inventoryValueDelta: number; cogsDelta: number; periodPnlDelta: number;
  nullReplayPassed: boolean; massBalanceCheckPassed: boolean; doubleEntryCheckPassed: boolean;
  movementComparisons: MovementCmp[]; detailComparisons: DetailCmp[];
  journalComparisons: JECmp[]; glComparisons: GLCmp[];
  impactedDocuments: ImpactedDoc[]; warnings: SimWarning[];
}
interface ApplyResult { isSuccess: boolean; operationId: number; message: string; messageAr: string; changeCount: number; financialImpact: number; appliedAt: string; rollbackEligibleUntil?: string; errorDetails?: string; }
interface OperationHistory { operationId: number; operationType: string; targetItemId: number; itemCode: string; itemName: string; itemNameAr: string; oldValue?: number; newValue?: number; changeCount: number; financialImpact: number; costingMethod: string; reason?: string; status: string; createdDate: string; createdByName?: string; appliedDate?: string; rolledBackDate?: string; rollbackEligibleUntil?: string; canRollback: boolean; }

interface ItemLocation { warehouseId?: number; warehouseName?: string; warehouseNameAr?: string; branchId?: number; branchName?: string; branchNameAr?: string; unitId: number; unitCode: string; unitName: string; currentQuantity: number; currentValue: number; movementCount: number; locationKey: string; locationLabel: string; }

interface AuditJeLine { entryDetailId: number; lineNumber: number; accountId: number; accountCode?: string; accountName?: string; accountNameAr?: string; oldDebit: number; newDebit: number; oldCredit: number; newCredit: number; }
interface AuditDoc { documentType: string; documentTypeAr: string; documentId: number; documentNumber: string; documentDate: string; status?: string; navigationPath?: string; quantity: number; oldUnitCost: number; newUnitCost: number; oldTotalCost: number; newTotalCost: number; documentDelta: number; journalEntryId?: number; journalEntryNumber?: string; journalEntryStatus?: string; journalNavigationPath?: string; jeOldTotalDebit?: number; jeNewTotalDebit?: number; jeOldTotalCredit?: number; jeNewTotalCredit?: number; jeDelta?: number; isMatching: boolean; matchExplanation?: string; jeLines: AuditJeLine[]; }
interface OperationAudit { operationId: number; operationType: string; status: string; itemId: number; itemCode: string; itemName: string; itemNameAr: string; reason?: string; financialImpact: number; createdDate: string; appliedDate?: string; createdByName?: string; totalDocuments: number; matchingDocuments: number; allMatch: boolean; documents: AuditDoc[]; }

// ============================================================
// Component
// ============================================================
const InventoryRebalancePage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [step, setStep] = useState(0); // 0..5
  const [loading, setLoading] = useState(false);
  const [busyApply, setBusyApply] = useState(false);

  // Step 1 — Diagnostic
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('All');
  const [searchText, setSearchText] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleExpand = (idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Step 2 — Lifecycle
  const [selectedIssue, setSelectedIssue] = useState<DiagnosticIssue | null>(null);
  const [lifecycle, setLifecycle] = useState<ItemLifecycle | null>(null);
  const [directItemCode, setDirectItemCode] = useState<string>('');

  // Step 3 — Correction input
  const [newCost, setNewCost] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [correctionTarget, setCorrectionTarget] = useState<{ sourceType: string; sourceId: number; sourceDetailId: number; currentCost: number } | null>(null);

  // Step 4 — Simulation
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [comparisonTab, setComparisonTab] = useState(0);

  // Step 5 — Confirmation
  const [confirmCode, setConfirmCode] = useState('');

  // Step 6 — Result
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<OperationHistory[]>([]);

  // Audit view
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState<OperationAudit | null>(null);
  const [auditJeOpen, setAuditJeOpen] = useState<AuditDoc | null>(null);

  // Location chooser (for Direct Item Inspection when item has multiple locations)
  const [locationOpen, setLocationOpen] = useState(false);
  const [locations, setLocations] = useState<ItemLocation[]>([]);
  const [locationItemId, setLocationItemId] = useState<number | null>(null);
  const [locationItemCode, setLocationItemCode] = useState<string>('');

  useEffect(() => { companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {}); }, []);

  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
  const reportTitle = isRTL ? 'إعادة ضبط المخزون' : 'Inventory Rebalance';

  // ===== labels =====
  const T = (en: string, ar: string) => isRTL ? ar : en;

  // ===== STEP 1 — diagnostic =====
  const runDiagnostic = async () => {
    setLoading(true); setReport(null);
    try {
      const { data } = await axiosInstance.get('/InventoryRebalance/diagnostic');
      setReport(data);
      toast.success(T(`Scan complete: ${data.totalIssuesFound} issue(s) found`, `اكتمل الفحص: ${data.totalIssuesFound} مشكلة`));
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Diagnostic failed', 'فشل الفحص'));
    } finally { setLoading(false); }
  };

  const filteredIssues = useMemo(() => {
    if (!report) return [];
    const q = searchText.trim().toLowerCase();
    return report.issues.filter(i =>
      (severityFilter === 'All' || i.severity === severityFilter) &&
      (!q || (i.itemCode?.toLowerCase().includes(q) || i.itemName?.toLowerCase().includes(q) || i.itemNameAr?.toLowerCase().includes(q) || i.issueType.toLowerCase().includes(q)))
    );
  }, [report, severityFilter, searchText]);

  // ===== STEP 2 — lifecycle =====
  const openIssue = async (issue: DiagnosticIssue) => {
    if (!issue.itemId) { toast.error(T('Issue has no item to inspect', 'لا يوجد صنف للفحص')); return; }
    setSelectedIssue(issue); setLifecycle(null); setLoading(true);
    try {
      const params = new URLSearchParams();
      if (issue.warehouseId) params.set('warehouseId', String(issue.warehouseId));
      if (issue.branchId) params.set('branchId', String(issue.branchId));
      const { data } = await axiosInstance.get(`/InventoryRebalance/lifecycle/${issue.itemId}?${params}`);
      setLifecycle(data);
      if (issue.canAutoCorrect && issue.expectedValue != null && issue.sourceId && issue.sourceDetailId) {
        setNewCost(String(issue.expectedValue));
        setCorrectionTarget({ sourceType: issue.sourceType || 'InventoryOpeningBalance', sourceId: issue.sourceId, sourceDetailId: issue.sourceDetailId, currentCost: issue.currentValue || 0 });
      } else {
        setNewCost('');
        setCorrectionTarget(null);
      }
      setReason('');
      setStep(1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Failed to load lifecycle', 'فشل تحميل دورة الحياة'));
    } finally { setLoading(false); }
  };

  // Direct item code search → location chooser → lifecycle
  const openDirectItem = async () => {
    const code = directItemCode.trim();
    if (!code) { toast.error(T('Enter an item code', 'أدخل كود الصنف')); return; }
    setLoading(true); setLifecycle(null);
    try {
      let itemId: number | undefined;
      if (report) {
        const m = report.issues.find(i => i.itemCode?.toLowerCase() === code.toLowerCase());
        if (m?.itemId) itemId = m.itemId;
      }
      if (!itemId) {
        try {
          const { data } = await axiosInstance.get(`/Items?search=${encodeURIComponent(code)}`);
          const arr = Array.isArray(data) ? data : (data?.data || data?.items || []);
          const found = arr.find((it: any) => (it.itemCode || it.ItemCode)?.toLowerCase() === code.toLowerCase()) || arr[0];
          if (found) itemId = found.itemId || found.ItemId;
        } catch { /* ignore */ }
      }
      if (!itemId) { toast.error(T('Item not found', 'الصنف غير موجود')); setLoading(false); return; }

      // Get all locations for this item — show chooser
      const { data: locs } = await axiosInstance.get<ItemLocation[]>(`/InventoryRebalance/item-locations/${itemId}`);
      if (!locs || locs.length === 0) {
        toast.warning(T('Item has no stock movements yet', 'الصنف ليس له حركات مخزون'));
        setLoading(false);
        return;
      }
      if (locs.length === 1) {
        // Only one location → go straight to lifecycle
        await loadLifecycleForLocation(itemId, code, locs[0]);
      } else {
        setLocations(locs);
        setLocationItemId(itemId);
        setLocationItemCode(code);
        setLocationOpen(true);
        setLoading(false);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Failed to load locations', 'فشل تحميل المواقع'));
      setLoading(false);
    }
  };

  const loadLifecycleForLocation = async (itemId: number, itemCode: string, loc: ItemLocation) => {
    setLoading(true); setLocationOpen(false);
    try {
      const params = new URLSearchParams();
      if (loc.warehouseId) params.set('warehouseId', String(loc.warehouseId));
      if (loc.branchId) params.set('branchId', String(loc.branchId));
      params.set('unitId', String(loc.unitId));
      const { data } = await axiosInstance.get(`/InventoryRebalance/lifecycle/${itemId}?${params}`);
      setLifecycle(data);
      setSelectedIssue({
        issueType: 'DirectInspection', issueTypeAr: 'فحص مباشر', severity: 'Info',
        itemId, itemCode, itemName: data.itemName, itemNameAr: data.itemNameAr,
        description: `Direct inspection at ${loc.locationLabel}`, descriptionAr: `فحص مباشر في ${loc.locationLabel}`,
        canAutoCorrect: false, affectedDocuments: [],
        warehouseId: loc.warehouseId, branchId: loc.branchId
      });
      setNewCost(''); setReason(''); setCorrectionTarget(null);
      setStep(1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Failed to load lifecycle', 'فشل تحميل دورة الحياة'));
    } finally { setLoading(false); }
  };

  // Map movement type → backend SourceType string
  const movementTypeToSourceType = (mt: string): string | null => {
    switch (mt) {
      case 'Opening':
      case 'OpeningBalance': return 'InventoryOpeningBalance';
      case 'Purchase': return 'PurchaseInvoice';
      case 'PurchaseReturn': return 'PurchaseReturn';
      case 'BranchIssue': return 'BranchIssue';
      case 'Transfer': return 'WarehouseTransfer';
      case 'BranchTransfer': return 'BranchTransfer';
      case 'InternalIssue': return 'InternalIssue';
      case 'StockCount': return 'StockCount';
      case 'Adjustment': return 'StockAdjustment';
      default: return null;
    }
  };

  // Movement types whose cost is user-entered (correctable directly)
  const isCorrectableType = (mt: string) =>
    ['Opening', 'OpeningBalance', 'Purchase', 'BranchIssue', 'BranchTransfer', 'WarehouseTransfer', 'Transfer', 'InternalIssue', 'StockCount', 'PurchaseReturn'].includes(mt);

  // From lifecycle event row → propose cost correction on that source
  const proposeCorrectionFromEvent = (e: LifecycleEvent) => {
    const sourceType = movementTypeToSourceType(e.movementType);
    if (!sourceType) {
      toast.info(T(`Cost correction not supported for movement type: ${e.movementType}`, `تصحيح التكلفة غير مدعوم لنوع الحركة: ${e.movementType}`));
      return;
    }
    if (!e.documentId || !e.documentDetailId) {
      toast.error(T('Missing document references for this row', 'بيانات السند غير مكتملة لهذا الصف'));
      return;
    }
    setCorrectionTarget({ sourceType, sourceId: e.documentId, sourceDetailId: e.documentDetailId, currentCost: e.unitCost });
    setNewCost(String(e.unitCost));
    setStep(2);
  };

  // ===== STEP 3→4 — simulate =====
  const runSimulation = async () => {
    const tgt = correctionTarget || (selectedIssue?.sourceId && selectedIssue?.sourceDetailId ? {
      sourceType: selectedIssue.sourceType || 'InventoryOpeningBalance',
      sourceId: selectedIssue.sourceId,
      sourceDetailId: selectedIssue.sourceDetailId,
      currentCost: selectedIssue.currentValue || 0
    } : null);
    if (!tgt) { toast.error(T('Pick an Opening Balance row from the lifecycle first', 'اختر سطر سند افتتاحي من دورة الحياة أولاً')); return; }
    const v = parseFloat(newCost);
    if (isNaN(v) || v < 0) { toast.error(T('Enter a valid new cost', 'أدخل تكلفة جديدة صحيحة')); return; }
    setLoading(true); setSimulation(null);
    try {
      const { data } = await axiosInstance.post('/InventoryRebalance/simulate', {
        itemId: selectedIssue?.itemId,
        operationType: 'CostCorrection',
        targetSourceType: tgt.sourceType,
        targetSourceId: tgt.sourceId,
        targetSourceDetailId: tgt.sourceDetailId,
        newValue: v,
        reason
      });
      setSimulation(data);
      setStep(3);
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Simulation failed', 'فشل المحاكاة'));
    } finally { setLoading(false); }
  };

  // ===== STEP 5 — apply =====
  const applyCorrection = async () => {
    if (!simulation || !selectedIssue || !correctionTarget) return;
    if (confirmCode.trim() !== (selectedIssue.itemCode || '')) { toast.error(T('Item code does not match', 'كود الصنف لا يطابق')); return; }
    setBusyApply(true);
    try {
      const { data } = await axiosInstance.post('/InventoryRebalance/apply', {
        itemId: selectedIssue.itemId,
        operationType: 'CostCorrection',
        targetSourceType: correctionTarget.sourceType,
        targetSourceId: correctionTarget.sourceId,
        targetSourceDetailId: correctionTarget.sourceDetailId,
        newValue: parseFloat(newCost),
        reason,
        confirmItemCode: confirmCode
      });
      setApplyResult(data);
      if (data.isSuccess) {
        toast.success(isRTL ? data.messageAr : data.message);
        setStep(5);
      } else {
        toast.error(isRTL ? data.messageAr : data.message);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Apply failed', 'فشل التطبيق'));
    } finally { setBusyApply(false); }
  };

  // ===== Cache rebuild (One-time global or per-item) =====
  const rebuildCache = async (itemId?: number) => {
    const confirmMsg = itemId
      ? T('Rebuild stock cache for this item? FIFO replay + StockLevels sync (atomic + 7-day rollback).', 'إعادة بناء كاش المخزون لهذا الصنف؟ إعادة سير FIFO + مزامنة StockLevels (ذرية + تراجع 7 أيام).')
      : T('Rebuild stock cache for ALL items? Walks every item against current OUT operations. This is a heavy operation.', 'إعادة بناء كاش المخزون لكل الأصناف؟ عملية ثقيلة.');
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      const url = itemId ? `/InventoryRebalance/rebuild-cache?itemId=${itemId}` : '/InventoryRebalance/rebuild-cache';
      const { data } = await axiosInstance.post(url);
      if (data.isSuccess) {
        toast.success(isRTL ? data.messageAr : data.message);
        if (report) runDiagnostic();
      } else {
        toast.error(isRTL ? data.messageAr : data.message);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Rebuild failed', 'فشلت إعادة البناء'));
    } finally { setLoading(false); }
  };

  // ===== History + Rollback =====
  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await axiosInstance.get('/InventoryRebalance/history');
      setHistory(data);
    } catch { /* ignore */ }
  };
  const openAudit = async (opId: number) => {
    try {
      const { data } = await axiosInstance.get(`/InventoryRebalance/audit/${opId}`);
      setAudit(data);
      setAuditOpen(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Failed to load audit', 'فشل تحميل المراجعة'));
    }
  };

  const rollbackOp = async (opId: number) => {
    if (!window.confirm(T('Rollback this operation?', 'هل تريد التراجع عن هذه العملية؟'))) return;
    try {
      const { data } = await axiosInstance.post(`/InventoryRebalance/rollback/${opId}`);
      if (data.isSuccess) { toast.success(isRTL ? data.messageAr : data.message); openHistory(); }
      else toast.error(isRTL ? data.messageAr : data.message);
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Rollback failed', 'فشل التراجع'));
    }
  };

  // ===== Reset wizard =====
  const startOver = () => {
    setStep(0); setSelectedIssue(null); setLifecycle(null); setSimulation(null);
    setNewCost(''); setReason(''); setConfirmCode(''); setApplyResult(null);
    setCorrectionTarget(null); setDirectItemCode('');
  };

  const openDoc = (path?: string) => { if (path) window.open(path, '_blank'); };
  const sevColor = (s: string) => s === 'Critical' ? '#f44336' : s === 'Warning' ? '#ff9800' : '#2196f3';
  const sevIcon = (s: string) => s === 'Critical' ? <ErrorIcon sx={{ color: '#f44336' }} /> : s === 'Warning' ? <WarnIcon sx={{ color: '#ff9800' }} /> : <OkIcon sx={{ color: '#2196f3' }} />;
  const fmt = (n?: number, d = 4) => n == null ? '-' : Number(n).toFixed(d);
  const fmtKWD = (n: number) => n.toFixed(3) + ' KWD';

  // ============================================================
  // EXPORT — Excel + PDF for the diagnostic report
  // ============================================================
  const exportDiagnosticExcel = async () => {
    if (!report) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch {}
      }
      ws.mergeCells('C1:G1'); const cc = ws.getCell('C1'); cc.value = compName || ''; cc.font = { size: 18, bold: true, color: { argb: 'FF7c3aed' } }; cc.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.mergeCells('A3:G3'); const tc = ws.getCell('A3'); tc.value = reportTitle; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7c3aed' } };
      ws.mergeCells('A4:G4'); const sc = ws.getCell('A4'); sc.value = `${T('Costing method', 'سياسة التكلفة')}: ${report.costingMethod} | ${T('Issues', 'مشاكل')}: ${report.totalIssuesFound}`; sc.alignment = { horizontal: 'center' };

      const headers = [T('Severity', 'الخطورة'), T('Type', 'النوع'), T('Item Code', 'كود الصنف'), T('Item Name', 'اسم الصنف'), T('Current', 'الحالي'), T('Expected', 'المتوقع'), T('Description', 'الوصف')];
      const hr = ws.getRow(6); headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7c3aed' } }; c.alignment = { horizontal: 'center' }; });
      filteredIssues.forEach((iss, i) => {
        const r = ws.getRow(7 + i);
        r.getCell(1).value = iss.severity;
        r.getCell(2).value = isRTL ? iss.issueTypeAr : iss.issueType;
        r.getCell(3).value = iss.itemCode || '';
        r.getCell(4).value = isRTL ? (iss.itemNameAr || '') : (iss.itemName || '');
        r.getCell(5).value = iss.currentValue ?? '';
        r.getCell(6).value = iss.expectedValue ?? '';
        r.getCell(7).value = isRTL ? iss.descriptionAr : iss.description;
        const bg = iss.severity === 'Critical' ? 'FFFFEBEE' : iss.severity === 'Warning' ? 'FFFFF3E0' : 'FFE3F2FD';
        r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }; });
      });
      ws.columns = [{ width: 12 }, { width: 28 }, { width: 14 }, { width: 30 }, { width: 14 }, { width: 14 }, { width: 60 }];
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `InventoryRebalance_${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success(T('Exported', 'تم التصدير'));
    } catch { toast.error(T('Export failed', 'فشل التصدير')); }
  };

  const exportDiagnosticPdf = () => {
    if (!report) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      doc.setFillColor(245, 240, 255); doc.rect(0, 0, pw, 45, 'F');
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(124, 58, 237);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(124, 58, 237); doc.roundedRect(pw / 2 - 55, 32, 110, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255); doc.text(reportTitle, pw / 2, 38.5, { align: 'center' });

      autoTable(doc, {
        head: [[T('Severity', 'Severity'), T('Type', 'Type'), T('Item Code', 'Item'), T('Item Name', 'Name'), T('Current', 'Current'), T('Expected', 'Expected'), T('Description', 'Description')]],
        body: filteredIssues.map(iss => [iss.severity, iss.issueType, iss.itemCode || '', iss.itemName || '', fmt(iss.currentValue), fmt(iss.expectedValue), iss.description]),
        startY: 50, theme: 'grid',
        headStyles: { fillColor: [124, 58, 237], textColor: [255, 255, 255], fontSize: 9, halign: 'center' },
        bodyStyles: { fontSize: 8, textColor: [50, 50, 50] }
      });
      doc.save(`InventoryRebalance_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(T('Exported', 'تم التصدير'));
    } catch { toast.error(T('Export failed', 'فشل التصدير')); }
  };

  const printDiagnostic = () => {
    if (!report) return;
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) return;
    const rows = filteredIssues.map(i => `<tr class="${i.severity.toLowerCase()}"><td>${i.severity}</td><td>${isRTL ? i.issueTypeAr : i.issueType}</td><td>${i.itemCode || ''}</td><td>${isRTL ? (i.itemNameAr || '') : (i.itemName || '')}</td><td style="text-align:right">${fmt(i.currentValue)}</td><td style="text-align:right">${fmt(i.expectedValue)}</td><td>${isRTL ? i.descriptionAr : i.description}</td></tr>`).join('');
    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:18px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #7c3aed;padding-bottom:10px;margin-bottom:15px}
      .header img{max-height:60px} .header h1{color:#7c3aed;margin:0;font-size:22px} .header .sub{color:#666;font-size:12px}
      .stats{display:flex;gap:12px;margin-bottom:15px}
      .stat{flex:1;padding:12px 16px;border-radius:10px;color:white;text-align:center}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{background:#7c3aed;color:white;padding:8px;text-align:${isRTL ? 'right' : 'left'}}
      td{padding:6px 8px;border:1px solid #ddd}
      .critical{background:#ffebee} .warning{background:#fff3e0} .info{background:#e3f2fd}
      @media print{.stat{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
    <div style="text-align:center;flex:1"><h1>${reportTitle}</h1><div class="sub">${compName || ''}</div></div><div style="width:80px"></div></div>
    <div class="stats">
      <div class="stat" style="background:#7c3aed"><div style="font-size:11px">${T('Costing', 'السياسة')}</div><div style="font-size:18px;font-weight:bold">${report.costingMethod}</div></div>
      <div class="stat" style="background:#f44336"><div style="font-size:11px">${T('Critical', 'حرج')}</div><div style="font-size:24px;font-weight:bold">${report.criticalCount}</div></div>
      <div class="stat" style="background:#ff9800"><div style="font-size:11px">${T('Warning', 'تحذير')}</div><div style="font-size:24px;font-weight:bold">${report.warningCount}</div></div>
      <div class="stat" style="background:#2196f3"><div style="font-size:11px">${T('Info', 'معلوماتي')}</div><div style="font-size:24px;font-weight:bold">${report.infoCount}</div></div>
    </div>
    <table><thead><tr><th>${T('Severity', 'الخطورة')}</th><th>${T('Type', 'النوع')}</th><th>${T('Item', 'الصنف')}</th><th>${T('Name', 'الاسم')}</th><th>${T('Current', 'الحالي')}</th><th>${T('Expected', 'المتوقع')}</th><th>${T('Description', 'الوصف')}</th></tr></thead><tbody>${rows}</tbody></table>
    <div style="text-align:center;margin-top:15px;color:#999;font-size:10px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  // ============================================================
  // STEP UI
  // ============================================================
  const steps = [
    T('Diagnostic Scan', 'فحص تشخيصي'),
    T('Lifecycle', 'دورة الحياة'),
    T('Correction', 'التصحيح'),
    T('Simulation', 'المحاكاة'),
    T('Confirm', 'التأكيد'),
    T('Result', 'النتيجة')
  ];

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {T('Global diagnostic + cascading correction tool — atomic, reversible, costing-policy aware.', 'أداة تشخيص شامل وتصحيح متسلسل — تطبيق ذري وقابل للتراجع وتحترم سياسة التكلفة.')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={openHistory} sx={{ borderRadius: 3 }}>
            {T('History', 'السجل')}
          </Button>
          {step !== 0 && (
            <Button variant="outlined" onClick={startOver} sx={{ borderRadius: 3 }}>
              {T('Start Over', 'إعادة البدء')}
            </Button>
          )}
        </Stack>
      </Box>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((s, i) => <Step key={i}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {/* ===== STEP 0 — Diagnostic ===== */}
      {step === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight="bold">{T('Full-Database Diagnostic', 'تشخيص شامل لقاعدة البيانات')}</Typography>
                <Typography variant="body2" color="text.secondary">{T('Scans every item, every movement, every JE for inconsistencies.', 'فحص كل صنف وكل حركة وكل قيد بحثاً عن أي اختلال.')}</Typography>
              </Box>
              <Button variant="contained" size="large" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={runDiagnostic} disabled={loading}
                sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', px: 4, py: 1.5, borderRadius: 3, fontWeight: 'bold' }}>
                {loading ? T('Scanning...', 'جاري الفحص...') : T('Run Diagnostic Now', 'ابدأ الفحص الآن')}
              </Button>
            </Stack>
          </Paper>

          {/* Direct item inspection (no diagnostic needed) */}
          <Paper sx={{ p: 2.5, mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(236,72,153,0.06) 100%)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">{T('Direct Item Inspection', 'فحص مباشر لأي صنف')}</Typography>
                <Typography variant="caption" color="text.secondary">{T('Enter an item code to view its full lifecycle and propose a cost correction on any of its Opening Balance entries — even if no diagnostic issue exists.', 'أدخل كود الصنف لرؤية دورة حياته الكاملة واقتراح تصحيح تكلفة على أي سند افتتاحي حتى لو لم يظهر أي اختلال في الفحص.')}</Typography>
              </Box>
              <TextField
                size="small" placeholder={T('e.g. ITEM-000153', 'مثال: ITEM-000153')}
                value={directItemCode} onChange={(e) => setDirectItemCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') openDirectItem(); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: 240, bgcolor: 'background.paper', borderRadius: 1 }}
              />
              <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LifecycleIcon />}
                onClick={openDirectItem} disabled={loading || !directItemCode.trim()}
                sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', borderRadius: 3, px: 3 }}>
                {T('Inspect Item', 'افحص الصنف')}
              </Button>
            </Stack>
          </Paper>

          {report && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Card sx={{ flex: 1, minWidth: 180, background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Costing Method', 'سياسة التكلفة')}</Typography>
                    <Typography variant="h5" fontWeight="bold">{report.costingMethod}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 180, background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', color: 'white', borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Critical', 'حرج')}</Typography>
                    <Typography variant="h3" fontWeight="bold">{report.criticalCount}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 180, background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white', borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Warning', 'تحذير')}</Typography>
                    <Typography variant="h3" fontWeight="bold">{report.warningCount}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 180, background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', color: 'white', borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Info', 'معلوماتي')}</Typography>
                    <Typography variant="h3" fontWeight="bold">{report.infoCount}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 180, background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', color: 'white', borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Items Scanned', 'أصناف مفحوصة')}</Typography>
                    <Typography variant="h3" fontWeight="bold">{report.totalItemsScanned}</Typography>
                  </CardContent>
                </Card>
              </Stack>

              <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1}>
                    {(['All', 'Critical', 'Warning', 'Info'] as const).map(s => (
                      <Chip key={s} label={T(s, s === 'All' ? 'الكل' : s === 'Critical' ? 'حرج' : s === 'Warning' ? 'تحذير' : 'معلوماتي')}
                        onClick={() => setSeverityFilter(s)}
                        color={severityFilter === s ? 'primary' : 'default'}
                        variant={severityFilter === s ? 'filled' : 'outlined'} />
                    ))}
                  </Stack>
                  <TextField
                    size="small" placeholder={T('Search item/type...', 'بحث صنف/نوع...')}
                    value={searchText} onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ minWidth: 280 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<RefreshIcon />} onClick={() => rebuildCache()} variant="contained"
                      sx={{ background: 'linear-gradient(135deg,#0891b2 0%,#06b6d4 100%)', borderRadius: 3 }}>
                      {T('Rebuild All Caches', 'إعادة بناء كل الكاش')}
                    </Button>
                    <Button startIcon={<ExcelIcon />} onClick={exportDiagnosticExcel} variant="contained" sx={{ background: 'linear-gradient(45deg,#4CAF50 30%,#66BB6A 90%)', borderRadius: 3 }}>Excel</Button>
                    <Button startIcon={<PdfIcon />} onClick={exportDiagnosticPdf} variant="contained" sx={{ background: 'linear-gradient(45deg,#f44336 30%,#e57373 90%)', borderRadius: 3 }}>PDF</Button>
                    <Button startIcon={<PrintIcon />} onClick={printDiagnostic} variant="outlined" sx={{ borderRadius: 3 }}>{T('Print', 'طباعة')}</Button>
                  </Stack>
                </Stack>
              </Paper>

              <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{T('Severity', 'الخطورة')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{T('Type', 'النوع')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{T('Item', 'الصنف')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{T('Current', 'الحالي')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{T('Expected', 'المتوقع')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{T('Description', 'الوصف')}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{T('Action', 'إجراء')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIssues.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">{T('No issues match the current filter', 'لا توجد مشاكل تطابق الفلتر')}</Typography></TableCell></TableRow>
                    )}
                    {filteredIssues.map((i, idx) => {
                      const hasRootCause = !!(i.rootCauseStory || i.causedByDocumentNumber || i.causedByUserName);
                      const isExpanded = expandedRows.has(idx);
                      return (
                      <React.Fragment key={idx}>
                      <TableRow hover sx={{ bgcolor: isDark
                        ? (i.severity === 'Critical' ? 'rgba(244,67,54,0.12)' : i.severity === 'Warning' ? 'rgba(255,152,0,0.12)' : 'rgba(33,150,243,0.10)')
                        : (i.severity === 'Critical' ? '#fff5f5' : i.severity === 'Warning' ? '#fffaf0' : '#f0f8ff') }}>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {hasRootCause && (
                              <IconButton size="small" onClick={() => toggleExpand(idx)}
                                sx={{ p: 0.25, color: '#7c3aed' }}
                                title={T('Why?', 'لماذا؟')}>
                                {isExpanded ? <span style={{ fontSize: 14 }}>▼</span> : <span style={{ fontSize: 14 }}>▶</span>}
                              </IconButton>
                            )}
                            <Chip size="small" label={i.severity} icon={sevIcon(i.severity)} sx={{ bgcolor: sevColor(i.severity), color: 'white', fontWeight: 'bold' }} />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{isRTL ? i.issueTypeAr : i.issueType}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>{i.itemCode}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{isRTL ? (i.itemNameAr || '') : (i.itemName || '')}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(i.currentValue)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(i.expectedValue)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>{isRTL ? i.descriptionAr : i.description}</Typography>
                          {hasRootCause && !isExpanded && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#7c3aed', fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => toggleExpand(idx)}>
                              {T('▶ Click to see root cause (who/when/why)', '▶ اضغط لمعرفة السبب الجذري (من/متى/لماذا)')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {i.issueType === 'StockLevelMismatch' && i.itemId ? (
                            <Button size="small" variant="contained" startIcon={<RefreshIcon />}
                              onClick={() => rebuildCache(i.itemId)}
                              sx={{ background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)', borderRadius: 2, color: 'white' }}>
                              {T('Rebuild Cache', 'إعادة بناء الكاش')}
                            </Button>
                          ) : (
                            <Button size="small" variant={i.canAutoCorrect ? 'contained' : 'outlined'} startIcon={<NextIcon />}
                              onClick={() => openIssue(i)}
                              disabled={!i.itemId}
                              sx={i.canAutoCorrect
                                ? { background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', borderRadius: 2, color: 'white' }
                                : { borderRadius: 2 }}>
                              {i.canAutoCorrect ? T('Inspect & Fix', 'فحص وتصحيح') : T('Inspect', 'فحص')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && hasRootCause && (
                        <TableRow sx={{ bgcolor: isDark ? 'rgba(124,58,237,0.08)' : '#faf5ff' }}>
                          <TableCell colSpan={7} sx={{ p: 2 }}>
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(124,58,237,0.10)' : '#fff', border: `1px solid ${isDark ? 'rgba(124,58,237,0.4)' : '#e9d5ff'}` }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#7c3aed', mb: 1.5 }}>
                                🔍 {T('Root Cause Analysis', 'تحليل السبب الجذري')}
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                                {i.causedByDocumentNumber && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Caused by document', 'المستند المُسبِّب')}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1976d2' }}>{i.causedByDocumentNumber}</Typography>
                                    <Typography variant="caption" color="text.secondary">{i.causedByDocumentType}</Typography>
                                  </Box>
                                )}
                                {i.causedByDocumentStatus && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Document status', 'حالة المستند')}</Typography>
                                    <Chip label={i.causedByDocumentStatus} size="small" sx={{ mt: 0.25, fontWeight: 700,
                                      bgcolor: i.causedByDocumentStatus === 'Posted' ? '#43a047' : '#fb8c00', color: '#fff' }} />
                                  </Box>
                                )}
                                {i.causedByPostedFromPage && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Posted from', 'تم الترحيل من')}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: i.causedByPostedFromPage === 'JournalEntriesPage' ? '#e65100' : '#1976d2' }}>
                                      {i.causedByPostedFromPage === 'JournalEntriesPage'
                                        ? T('Journal Entries page', 'صفحة قيود اليومية')
                                        : T('Source document page', 'صفحة المستند المنبع')}
                                    </Typography>
                                  </Box>
                                )}
                                {i.causedByDocumentDate && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Document date', 'تاريخ المستند')}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{i.causedByDocumentDate.substring(0, 10)}</Typography>
                                  </Box>
                                )}
                                {(i.causedByPostedByName || i.causedByUserName) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Posted by user', 'المستخدم الذي رحّل')}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#e53935' }}>{i.causedByPostedByName || i.causedByUserName}</Typography>
                                  </Box>
                                )}
                                {(i.causedByPostedAt || i.causedByCreatedAt) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('When posted', 'متى تم الترحيل')}</Typography>
                                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">{new Date(i.causedByPostedAt || i.causedByCreatedAt!).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' })}</Typography>
                                  </Box>
                                )}
                                {i.financialImpact != null && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Financial impact (KWD)', 'الأثر المالي (د.ك)')}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: (i.financialImpact ?? 0) < 0 ? '#e53935' : '#43a047' }}>
                                      {fmt(i.financialImpact)}
                                    </Typography>
                                  </Box>
                                )}
                                {i.affectedDocuments && i.affectedDocuments.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">{T('Affected documents', 'المستندات المتأثرة')}</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                      {i.affectedDocuments.map((d, di) => (
                                        <Chip key={di} label={d} size="small" sx={{ fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100' }} />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                              {(isRTL ? i.rootCauseStoryAr : i.rootCauseStory) && (
                                <Paper sx={{ p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa', borderLeft: '3px solid #7c3aed', mb: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {T('Story', 'الشرح')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                                    {isRTL ? i.rootCauseStoryAr : i.rootCauseStory}
                                  </Typography>
                                </Paper>
                              )}

                              {/* Recommended action + Navigate-to-source button */}
                              {(i.recommendedAction || i.causedByNavigationPath) && (
                                <Paper sx={{
                                  p: 1.5,
                                  bgcolor: i.recommendedAction === 'ManualFixAtSource'
                                    ? (isDark ? 'rgba(255,152,0,0.15)' : '#fff3e0')
                                    : (isDark ? 'rgba(67,160,71,0.15)' : '#e8f5e9'),
                                  borderLeft: `3px solid ${i.recommendedAction === 'ManualFixAtSource' ? '#ff9800' : '#43a047'}`
                                }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                                    <Box flex={1}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: i.recommendedAction === 'ManualFixAtSource' ? '#e65100' : '#2e7d32' }}>
                                        {i.recommendedAction === 'ManualFixAtSource'
                                          ? `⚠️ ${T('Recommended: Manual fix at source document', 'موصى به: إصلاح يدوي عند المستند المنبع')}`
                                          : `✓ ${T('Recommended: Auto-fix via Inspect & Fix', 'موصى به: إصلاح تلقائي عبر فحص وتصحيح')}`}
                                      </Typography>
                                      {(isRTL ? i.recommendedActionReasonAr : i.recommendedActionReason) && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, lineHeight: 1.6 }}>
                                          {isRTL ? i.recommendedActionReasonAr : i.recommendedActionReason}
                                        </Typography>
                                      )}
                                    </Box>
                                    {i.causedByNavigationPath && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => window.open(i.causedByNavigationPath!, '_blank', 'noopener,noreferrer')}
                                        sx={{
                                          background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                                          color: 'white',
                                          fontWeight: 'bold',
                                          minWidth: 220
                                        }}>
                                        🔗 {T(`Open ${i.causedByDocumentNumber} in new tab`, `فتح ${i.causedByDocumentNumber} في تبويب جديد`)}
                                      </Button>
                                    )}
                                  </Stack>
                                </Paper>
                              )}
                            </Paper>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* ===== STEP 1 — Lifecycle ===== */}
      {step === 1 && lifecycle && selectedIssue && (
        <>
          <Paper sx={{ p: 3, mb: 2, borderRadius: 3,
            background: isDark
              ? 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(236,72,153,0.10) 100%)'
              : 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%)',
            border: isDark ? '1px solid rgba(124,58,237,0.4)' : 'none' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <LifecycleIcon sx={{ fontSize: 40, color: '#7c3aed' }} />
              <Box flex={1}>
                <Typography variant="h6" fontWeight="bold">{lifecycle.itemCode} — {isRTL ? lifecycle.itemNameAr : lifecycle.itemName}</Typography>
                <Typography variant="body2" color="text.secondary">{T('Costing', 'السياسة')}: <b>{lifecycle.costingMethod}</b> · {T('Opening Qty', 'الكمية الافتتاحية')}: <b>{fmt(lifecycle.openingQuantity)}</b> · {T('Current Remaining', 'المتبقي حالياً')}: <b>{fmt(lifecycle.currentRemaining)}</b> · {T('Total Out', 'إجمالي صرف')}: <b>{fmt(lifecycle.totalOut)}</b></Typography>
              </Box>
            </Stack>
          </Paper>

          {lifecycle.events.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {T('No movements found for this item in the selected location.', 'لا توجد حركات لهذا الصنف في الموقع المحدد.')}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ borderRadius: 3, mb: 2, maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', color: 'white', fontWeight: 'bold' } }}>
                  <TableCell>#</TableCell>
                  <TableCell>{T('Date', 'التاريخ')}</TableCell>
                  <TableCell>{T('Type', 'النوع')}</TableCell>
                  <TableCell>{T('Document', 'المستند')}</TableCell>
                  <TableCell>{T('Location', 'الموقع')}</TableCell>
                  <TableCell align="right">{T('Qty In', 'وارد')}</TableCell>
                  <TableCell align="right">{T('Qty Out', 'صادر')}</TableCell>
                  <TableCell align="right">{T('Unit Cost', 'تكلفة الوحدة')}</TableCell>
                  <TableCell align="right">{T('Total Cost', 'الإجمالي')}</TableCell>
                  <TableCell align="right">{T('Balance', 'الرصيد')}</TableCell>
                  <TableCell align="center">{T('Action', 'إجراء')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lifecycle.events.map((e) => {
                  const isOpening = e.movementType === 'Opening' || e.movementType === 'OpeningBalance';
                  const correctable = isCorrectableType(e.movementType);
                  const isTarget = correctionTarget && correctionTarget.sourceDetailId === e.documentDetailId;
                  return (
                  <TableRow key={e.eventOrder} hover sx={isTarget ? { bgcolor: isDark ? 'rgba(124,58,237,0.18)' : '#f3e8ff' } : undefined}>
                    <TableCell sx={{ color: 'text.primary' }}>{e.eventOrder}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{new Date(e.eventDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                    <TableCell><Chip size="small" label={isRTL ? e.movementTypeAr : e.movementType} variant="outlined" color={isOpening ? 'primary' : 'default'} /></TableCell>
                    <TableCell>
                      {e.navigationPath ? (
                        <Button size="small" endIcon={<OpenIcon fontSize="small" />} onClick={() => openDoc(e.navigationPath)} sx={{ textTransform: 'none' }}>
                          {e.documentNumber}
                        </Button>
                      ) : <Typography variant="body2" sx={{ color: 'text.primary' }}>{e.documentNumber}</Typography>}
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{e.location || '-'}</TableCell>
                    <TableCell align="right" sx={{ color: e.qtyIn > 0 ? '#10b981' : 'text.primary', fontWeight: e.qtyIn > 0 ? 'bold' : 'normal' }}>{e.qtyIn > 0 ? fmt(e.qtyIn) : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: e.qtyOut > 0 ? '#ef4444' : 'text.primary', fontWeight: e.qtyOut > 0 ? 'bold' : 'normal' }}>{e.qtyOut > 0 ? fmt(e.qtyOut) : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(e.unitCost)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(e.totalCost, 3)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(e.runningBalance)}</TableCell>
                    <TableCell align="center">
                      {correctable && e.documentId && e.documentDetailId ? (
                        <Tooltip title={T(`Propose cost correction from this ${e.movementType}`, `اقتراح تصحيح تكلفة من هذا الـ ${e.movementTypeAr}`)}>
                          <Button size="small" variant={isTarget ? 'contained' : 'outlined'} color="primary"
                            onClick={() => proposeCorrectionFromEvent(e)}
                            sx={isTarget ? { background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' } : undefined}>
                            {isTarget ? T('✓ Selected', '✓ محدد') : T('Correct Cost', 'صحّح التكلفة')}
                          </Button>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Button startIcon={<BackIcon />} onClick={() => setStep(0)} variant="outlined">{T('Back', 'رجوع')}</Button>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'center' }}>
              {correctionTarget
                ? T(`Selected source: cost ${correctionTarget.currentCost.toFixed(4)}`, `المصدر المختار: التكلفة الحالية ${correctionTarget.currentCost.toFixed(4)}`)
                : T('Click "Correct Cost" on an Opening Balance row, or press Next to go without correction', 'اضغط "صحّح التكلفة" على سطر سند افتتاحي، أو تابع بدون تصحيح')}
            </Typography>
            <Button endIcon={<NextIcon />} onClick={() => setStep(2)} variant="contained" disabled={!correctionTarget}
              sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
              {T('Propose Correction', 'اقتراح تصحيح')}
            </Button>
          </Stack>
        </>
      )}

      {/* ===== STEP 2 — Propose Correction ===== */}
      {step === 2 && selectedIssue && (
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 700, mx: 'auto' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>{T('Cost Correction', 'تصحيح التكلفة')}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>{T('Provide the new value. The engine will simulate the cascading impact before any DB write.', 'أدخل القيمة الجديدة. سيقوم المحرك بمحاكاة الأثر المتسلسل قبل أي كتابة على القاعدة.')}</Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">{T('Item', 'الصنف')}</Typography>
              <Typography variant="body1" fontWeight="bold">{selectedIssue.itemCode} — {isRTL ? selectedIssue.itemNameAr : selectedIssue.itemName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{T('Current Unit Cost', 'التكلفة الحالية')}</Typography>
              <Typography variant="h5" color="error" fontWeight="bold">{fmt(correctionTarget?.currentCost ?? selectedIssue.currentValue)}</Typography>
            </Box>
            <TextField
              label={T('New Unit Cost', 'التكلفة الجديدة')}
              type="number" fullWidth value={newCost}
              onChange={(e) => setNewCost(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">KWD</InputAdornment> }}
            />
            <TextField
              label={T('Reason (required for audit log)', 'السبب (مطلوب لسجل التدقيق)')}
              multiline rows={3} fullWidth value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
            <Button startIcon={<BackIcon />} onClick={() => setStep(1)} variant="outlined">{T('Back', 'رجوع')}</Button>
            <Button endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SimIcon />} onClick={runSimulation} variant="contained" disabled={loading || !newCost || !reason}
              sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
              {loading ? T('Simulating...', 'جاري المحاكاة...') : T('Run Simulation', 'تشغيل المحاكاة')}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ===== STEP 3 — Simulation result ===== */}
      {step === 3 && simulation && (
        <>
          <Paper sx={{ p: 3, mb: 2, borderRadius: 3,
            background: simulation.isValid
              ? (isDark ? 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.08) 100%)' : 'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%)')
              : (isDark ? 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(239,68,68,0.08) 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)'),
            border: simulation.isValid ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(239,68,68,0.4)' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {simulation.isValid ? <VerifiedIcon sx={{ fontSize: 40, color: '#10b981' }} /> : <ErrorIcon sx={{ fontSize: 40, color: '#ef4444' }} />}
              <Box flex={1}>
                <Typography variant="h6" fontWeight="bold">
                  {simulation.isValid ? T('Simulation passed — safe to apply', 'المحاكاة نجحت — التطبيق آمن') : T('Simulation rejected', 'تم رفض المحاكاة')}
                </Typography>
                {!simulation.isValid && <Typography variant="body2" color="error">{isRTL ? simulation.validationErrorAr : simulation.validationError}</Typography>}
                <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                  <Chip size="small" label={`${T('Costing', 'السياسة')}: ${simulation.costingMethod}`} />
                  <Chip size="small" label={`${T('Movements', 'حركات')}: ${simulation.affectedMovementCount}`} color="primary" />
                  <Chip size="small" label={`${T('Issue Details', 'صرفيات')}: ${simulation.affectedIssueDetailCount}`} color="primary" />
                  <Chip size="small" label={`${T('JEs', 'قيود')}: ${simulation.affectedJournalEntryCount}`} color="primary" />
                  <Chip size="small" label={`${T('GL Rows', 'دفتر أستاذ')}: ${simulation.affectedGLRowCount}`} color="primary" />
                  <Chip size="small" icon={simulation.doubleEntryCheckPassed ? <OkIcon /> : <ErrorIcon />}
                    label={simulation.doubleEntryCheckPassed ? T('Double-entry OK', 'توازن المدين/الدائن ✓') : T('Double-entry FAIL', 'توازن المدين/الدائن ✗')}
                    color={simulation.doubleEntryCheckPassed ? 'success' : 'error'} />
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Card sx={{ flex: 1, minWidth: 200, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{T('Inventory Value Δ', 'تغير قيمة المخزون')}</Typography>
                <Typography variant="h5" fontWeight="bold" color={simulation.inventoryValueDelta >= 0 ? '#2e7d32' : '#c62828'}>{simulation.inventoryValueDelta >= 0 ? '+' : ''}{fmtKWD(simulation.inventoryValueDelta)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{T('COGS Δ', 'تغير تكلفة المبيعات')}</Typography>
                <Typography variant="h5" fontWeight="bold" color={simulation.cogsDelta >= 0 ? '#c62828' : '#2e7d32'}>{simulation.cogsDelta >= 0 ? '+' : ''}{fmtKWD(simulation.cogsDelta)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{T('Period P&L Δ', 'تغير ربح الفترة')}</Typography>
                <Typography variant="h5" fontWeight="bold" color={simulation.periodPnlDelta >= 0 ? '#2e7d32' : '#c62828'}>{simulation.periodPnlDelta >= 0 ? '+' : ''}{fmtKWD(simulation.periodPnlDelta)}</Typography>
              </CardContent>
            </Card>
          </Stack>

          {simulation.warnings && simulation.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {simulation.warnings.map((w, i) => <div key={i}>{isRTL ? w.messageAr : w.message}</div>)}
            </Alert>
          )}

          <Paper sx={{ borderRadius: 3, mb: 2 }}>
            <Tabs value={comparisonTab} onChange={(_, v) => setComparisonTab(v)} variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { fontWeight: 'bold' } }}>
              <Tab icon={<InvIcon />} label={T(`Movements (${simulation.movementComparisons.length})`, `حركات (${simulation.movementComparisons.length})`)} />
              <Tab icon={<JEIcon />} label={T(`Issue Details (${simulation.detailComparisons.length})`, `صرفيات (${simulation.detailComparisons.length})`)} />
              <Tab icon={<AcctIcon />} label={T(`Journal Entries (${simulation.journalComparisons.length})`, `قيود محاسبية (${simulation.journalComparisons.length})`)} />
              <Tab icon={<AcctIcon />} label={T(`GL Rows (${simulation.glComparisons.length})`, `دفتر أستاذ (${simulation.glComparisons.length})`)} />
              <Tab icon={<OpenIcon />} label={T(`Impacted Docs (${simulation.impactedDocuments.length})`, `مستندات متأثرة (${simulation.impactedDocuments.length})`)} />
            </Tabs>

            {/* Movements */}
            {comparisonTab === 0 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow><TableCell>#</TableCell><TableCell>{T('Date', 'التاريخ')}</TableCell><TableCell>{T('Source', 'المصدر')}</TableCell><TableCell align="right">{T('Qty', 'الكمية')}</TableCell><TableCell align="right">{T('Old Unit', 'تكلفة قديمة')}</TableCell><TableCell align="right">{T('New Unit', 'تكلفة جديدة')}</TableCell><TableCell align="right">{T('Old Total', 'إجمالي قديم')}</TableCell><TableCell align="right">{T('New Total', 'إجمالي جديد')}</TableCell><TableCell align="right">Δ</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {simulation.movementComparisons.map((m) => (
                      <TableRow key={m.movementId} hover>
                        <TableCell>{m.movementId}</TableCell>
                        <TableCell>{new Date(m.movementDate).toLocaleDateString()}</TableCell>
                        <TableCell><Chip size="small" label={m.sourceType} /></TableCell>
                        <TableCell align="right">{fmt(m.quantity)}</TableCell>
                        <TableCell align="right" sx={{ color: '#c62828' }}>{fmt(m.oldUnitCost)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{fmt(m.newUnitCost)}</TableCell>
                        <TableCell align="right" sx={{ color: '#c62828' }}>{fmt(m.oldTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{fmt(m.newTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: m.delta >= 0 ? '#2e7d32' : '#c62828' }}>{m.delta >= 0 ? '+' : ''}{fmt(m.delta, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Issue Details */}
            {comparisonTab === 1 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow><TableCell>{T('Document', 'المستند')}</TableCell><TableCell>{T('Date', 'التاريخ')}</TableCell><TableCell align="right">{T('Qty', 'الكمية')}</TableCell><TableCell align="right">{T('Old Unit', 'قديم')}</TableCell><TableCell align="right">{T('New Unit', 'جديد')}</TableCell><TableCell align="right">{T('Old Total', 'إجمالي قديم')}</TableCell><TableCell align="right">{T('New Total', 'إجمالي جديد')}</TableCell><TableCell align="right">Δ</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {simulation.detailComparisons.map((d) => (
                      <TableRow key={d.detailId} hover>
                        <TableCell><Button size="small" endIcon={<OpenIcon fontSize="small" />} onClick={() => openDoc(d.navigationPath)} sx={{ textTransform: 'none' }}>{d.documentNumber}</Button></TableCell>
                        <TableCell>{new Date(d.documentDate).toLocaleDateString()}</TableCell>
                        <TableCell align="right">{fmt(d.quantity)}</TableCell>
                        <TableCell align="right" sx={{ color: '#c62828' }}>{fmt(d.oldUnitCost)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{fmt(d.newUnitCost)}</TableCell>
                        <TableCell align="right" sx={{ color: '#c62828' }}>{fmt(d.oldTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{fmt(d.newTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: d.delta >= 0 ? '#2e7d32' : '#c62828' }}>{d.delta >= 0 ? '+' : ''}{fmt(d.delta, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Journal Entries */}
            {comparisonTab === 2 && (
              <Box sx={{ p: 2, maxHeight: 450, overflow: 'auto' }}>
                {simulation.journalComparisons.map((j) => (
                  <Paper key={j.journalEntryId} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          <Button onClick={() => openDoc(j.navigationPath)} sx={{ textTransform: 'none', p: 0 }} endIcon={<OpenIcon fontSize="small" />}>{j.entryNumber}</Button>
                          <Chip size="small" label={j.status} sx={{ ml: 1 }} color={j.status === 'Posted' ? 'success' : 'default'} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{new Date(j.entryDate).toLocaleDateString()}</Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary">{T('Total Δ', 'الفرق')}</Typography>
                        <Typography variant="h6" fontWeight="bold" color={j.delta >= 0 ? '#2e7d32' : '#c62828'}>{j.delta >= 0 ? '+' : ''}{fmt(j.delta, 3)}</Typography>
                      </Box>
                    </Stack>
                    <Table size="small">
                      <TableHead><TableRow sx={{ '& th': { bgcolor: '#f9fafb' } }}><TableCell>#</TableCell><TableCell>{T('Account', 'الحساب')}</TableCell><TableCell align="right">{T('Old D', 'مدين قديم')}</TableCell><TableCell align="right">{T('New D', 'مدين جديد')}</TableCell><TableCell align="right">{T('Old C', 'دائن قديم')}</TableCell><TableCell align="right">{T('New C', 'دائن جديد')}</TableCell></TableRow></TableHead>
                      <TableBody>
                        {j.lines.map((ln) => (
                          <TableRow key={ln.entryDetailId}>
                            <TableCell>{ln.lineNumber}</TableCell>
                            <TableCell><Typography variant="caption" fontWeight="bold">{ln.accountCode}</Typography><Typography variant="caption" display="block" color="text.secondary">{isRTL ? ln.accountNameAr : ln.accountName}</Typography></TableCell>
                            <TableCell align="right" sx={{ color: ln.newDebit !== ln.oldDebit ? '#c62828' : 'inherit' }}>{fmt(ln.oldDebit, 3)}</TableCell>
                            <TableCell align="right" sx={{ color: ln.newDebit !== ln.oldDebit ? '#2e7d32' : 'inherit', fontWeight: ln.newDebit !== ln.oldDebit ? 'bold' : 'normal' }}>{fmt(ln.newDebit, 3)}</TableCell>
                            <TableCell align="right" sx={{ color: ln.newCredit !== ln.oldCredit ? '#c62828' : 'inherit' }}>{fmt(ln.oldCredit, 3)}</TableCell>
                            <TableCell align="right" sx={{ color: ln.newCredit !== ln.oldCredit ? '#2e7d32' : 'inherit', fontWeight: ln.newCredit !== ln.oldCredit ? 'bold' : 'normal' }}>{fmt(ln.newCredit, 3)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                ))}
              </Box>
            )}

            {/* GL */}
            {comparisonTab === 3 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>{T('Date', 'التاريخ')}</TableCell><TableCell>JE</TableCell><TableCell>{T('Account', 'الحساب')}</TableCell><TableCell align="right">{T('Old D', 'مدين قديم')}</TableCell><TableCell align="right">{T('New D', 'مدين جديد')}</TableCell><TableCell align="right">{T('Old C', 'دائن قديم')}</TableCell><TableCell align="right">{T('New C', 'دائن جديد')}</TableCell></TableRow></TableHead>
                  <TableBody>
                    {simulation.glComparisons.map((g) => (
                      <TableRow key={g.ledgerId} hover>
                        <TableCell>{new Date(g.transactionDate).toLocaleDateString()}</TableCell>
                        <TableCell>{g.journalEntryNumber}</TableCell>
                        <TableCell><b>{g.accountCode}</b> <span style={{ color: '#666' }}>{isRTL ? g.accountNameAr : g.accountName}</span></TableCell>
                        <TableCell align="right" sx={{ color: g.newDebit !== g.oldDebit ? '#c62828' : 'inherit' }}>{fmt(g.oldDebit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: g.newDebit !== g.oldDebit ? '#2e7d32' : 'inherit', fontWeight: g.newDebit !== g.oldDebit ? 'bold' : 'normal' }}>{fmt(g.newDebit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: g.newCredit !== g.oldCredit ? '#c62828' : 'inherit' }}>{fmt(g.oldCredit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: g.newCredit !== g.oldCredit ? '#2e7d32' : 'inherit', fontWeight: g.newCredit !== g.oldCredit ? 'bold' : 'normal' }}>{fmt(g.newCredit, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Impacted Documents */}
            {comparisonTab === 4 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>{T('Type', 'النوع')}</TableCell><TableCell>{T('Number', 'الرقم')}</TableCell><TableCell>{T('Date', 'التاريخ')}</TableCell><TableCell>{T('Status', 'الحالة')}</TableCell><TableCell align="right">Δ</TableCell><TableCell>{T('Open', 'فتح')}</TableCell></TableRow></TableHead>
                  <TableBody>
                    {simulation.impactedDocuments.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell><Chip size="small" label={isRTL ? d.documentTypeAr : d.documentType} /></TableCell>
                        <TableCell>{d.documentNumber}</TableCell>
                        <TableCell>{new Date(d.documentDate).toLocaleDateString()}</TableCell>
                        <TableCell><Chip size="small" label={d.status} color={d.status === 'Posted' ? 'success' : 'default'} /></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: d.delta >= 0 ? '#2e7d32' : '#c62828' }}>{d.delta >= 0 ? '+' : ''}{fmt(d.delta, 3)}</TableCell>
                        <TableCell><IconButton size="small" onClick={() => openDoc(d.navigationPath)}><OpenIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button startIcon={<BackIcon />} onClick={() => setStep(2)} variant="outlined">{T('Back', 'رجوع')}</Button>
            <Button endIcon={<NextIcon />} onClick={() => setStep(4)} variant="contained" disabled={!simulation.isValid}
              sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
              {T('Proceed to Confirm', 'انتقل للتأكيد')}
            </Button>
          </Stack>
        </>
      )}

      {/* ===== STEP 4 — Confirm ===== */}
      {step === 4 && simulation && selectedIssue && (
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 600, mx: 'auto' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <ApplyIcon sx={{ fontSize: 40, color: '#7c3aed' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">{T('Final Confirmation', 'التأكيد النهائي')}</Typography>
              <Typography variant="caption" color="text.secondary">{T('This will write to the database inside an atomic transaction.', 'سيتم الكتابة على قاعدة البيانات داخل معاملة ذرية.')}</Typography>
            </Box>
          </Stack>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <b>{T('Re-type the item code', 'أعد كتابة كود الصنف')}</b>: <code>{selectedIssue.itemCode}</code>
            </Typography>
          </Alert>

          <TextField
            label={T('Confirm Item Code', 'تأكيد كود الصنف')}
            fullWidth value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)}
            error={confirmCode.length > 0 && confirmCode !== selectedIssue.itemCode}
            helperText={confirmCode.length > 0 && confirmCode !== selectedIssue.itemCode ? T('Does not match', 'غير مطابق') : ''}
          />

          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
            <Button startIcon={<BackIcon />} onClick={() => setStep(3)} variant="outlined">{T('Back', 'رجوع')}</Button>
            <Button startIcon={busyApply ? <CircularProgress size={20} color="inherit" /> : <ApplyIcon />}
              onClick={applyCorrection} variant="contained" disabled={busyApply || confirmCode !== selectedIssue.itemCode}
              sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', px: 4 }}>
              {busyApply ? T('Applying...', 'جاري التطبيق...') : T('Apply Correction', 'تطبيق التصحيح')}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ===== STEP 5 — Result ===== */}
      {step === 5 && applyResult && (
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 700, mx: 'auto', textAlign: 'center',
          background: applyResult.isSuccess
            ? (isDark ? 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.08) 100%)' : 'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%)')
            : (isDark ? 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(239,68,68,0.08) 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)'),
          border: applyResult.isSuccess ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(239,68,68,0.4)' }}>
          {applyResult.isSuccess ? <OkIcon sx={{ fontSize: 80, color: '#10b981' }} /> : <ErrorIcon sx={{ fontSize: 80, color: '#ef4444' }} />}
          <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: isDark ? '#fff' : '#111827' }}>{isRTL ? applyResult.messageAr : applyResult.message}</Typography>
          {applyResult.isSuccess && (
            <>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3, flexWrap: 'wrap' }}>
                <Chip label={`${T('Operation ID', 'رقم العملية')}: #${applyResult.operationId}`} color="primary" />
                <Chip label={`${T('Cells modified', 'خلايا معدلة')}: ${applyResult.changeCount}`} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb', color: isDark ? '#fff' : '#111827', fontWeight: 'bold' }} />
                <Chip label={`${T('Financial impact', 'الأثر المالي')}: ${fmtKWD(applyResult.financialImpact)}`} color="success" />
              </Stack>
              <Typography variant="caption" display="block" sx={{ mt: 2, color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280' }}>
                {T('Rollback eligible until', 'يمكن التراجع حتى')}: {applyResult.rollbackEligibleUntil ? new Date(applyResult.rollbackEligibleUntil).toLocaleString(isRTL ? 'ar-KW' : 'en-GB') : '-'}
              </Typography>
            </>
          )}
          <Button variant="contained" sx={{ mt: 4, background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }} onClick={startOver}>
            {T('Start New Operation', 'بدء عملية جديدة')}
          </Button>
        </Paper>
      )}

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle><HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{T('Rebalance History', 'سجل عمليات إعادة الضبط')}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>#</TableCell><TableCell>{T('Item', 'الصنف')}</TableCell><TableCell align="right">{T('Old', 'قديم')}</TableCell><TableCell align="right">{T('New', 'جديد')}</TableCell><TableCell align="right">{T('Impact', 'الأثر')}</TableCell><TableCell>{T('Status', 'الحالة')}</TableCell><TableCell>{T('When', 'الوقت')}</TableCell><TableCell>{T('By', 'بواسطة')}</TableCell><TableCell>{T('Action', 'إجراء')}</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 && <TableRow><TableCell colSpan={9} align="center"><Typography color="text.secondary">{T('No history yet', 'لا يوجد سجل بعد')}</Typography></TableCell></TableRow>}
                {history.map((h) => (
                  <TableRow key={h.operationId}>
                    <TableCell>#{h.operationId}</TableCell>
                    <TableCell><b>{h.itemCode}</b> <span style={{ color: '#666' }}>{isRTL ? h.itemNameAr : h.itemName}</span></TableCell>
                    <TableCell align="right">{fmt(h.oldValue)}</TableCell>
                    <TableCell align="right">{fmt(h.newValue)}</TableCell>
                    <TableCell align="right">{fmtKWD(h.financialImpact)}</TableCell>
                    <TableCell><Chip size="small" label={h.status} color={h.status === 'Applied' ? 'success' : h.status === 'RolledBack' ? 'warning' : 'default'} /></TableCell>
                    <TableCell>{new Date(h.createdDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                    <TableCell>{h.createdByName || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={T('View audit details', 'عرض المراجعة')}>
                          <IconButton size="small" color="primary" onClick={() => openAudit(h.operationId)}><VerifiedIcon /></IconButton>
                        </Tooltip>
                        {h.canRollback && (
                          <Tooltip title={T('Rollback', 'تراجع')}>
                            <IconButton size="small" color="warning" onClick={() => rollbackOp(h.operationId)}><RollbackIcon /></IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{T('Close', 'إغلاق')}</Button>
        </DialogActions>
      </Dialog>

      {/* Audit Dialog */}
      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VerifiedIcon color="primary" />
            <span>{T('Operation Audit', 'مراجعة العملية')} #{audit?.operationId}</span>
            {audit?.allMatch && <Chip size="small" icon={<OkIcon />} label={T('All Verified', 'كل شيء مطابق')} color="success" />}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {audit && (
            <>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: audit.allMatch
                ? (isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5')
                : (isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2'),
                border: audit.allMatch ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(239,68,68,0.4)' }}>
                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                  {audit.allMatch ? <OkIcon sx={{ fontSize: 40, color: '#10b981' }} /> : <ErrorIcon sx={{ fontSize: 40, color: '#ef4444' }} />}
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                      {audit.allMatch
                        ? T(`✓ All ${audit.matchingDocuments} of ${audit.totalDocuments} documents reconcile with their JEs`, `✓ كل ${audit.matchingDocuments} من ${audit.totalDocuments} مستند مطابق لقيوده`)
                        : T(`⚠ ${audit.matchingDocuments} of ${audit.totalDocuments} documents match`, `⚠ ${audit.matchingDocuments} من ${audit.totalDocuments} مستند مطابق`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {audit.itemCode} — {isRTL ? audit.itemNameAr : audit.itemName} · {T('Impact', 'الأثر')}: {fmtKWD(audit.financialImpact)} · {T('By', 'بواسطة')}: {audit.createdByName || '-'}
                    </Typography>
                    {audit.reason && <Typography variant="caption" display="block" color="text.secondary">{T('Reason', 'السبب')}: {audit.reason}</Typography>}
                  </Box>
                </Stack>
              </Paper>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', color: 'white', fontWeight: 'bold' } }}>
                      <TableCell>{T('Document', 'المستند')}</TableCell>
                      <TableCell>{T('Type', 'النوع')}</TableCell>
                      <TableCell>{T('Date', 'التاريخ')}</TableCell>
                      <TableCell align="right">{T('Old Total', 'الإجمالي قديم')}</TableCell>
                      <TableCell align="right">{T('New Total', 'الإجمالي جديد')}</TableCell>
                      <TableCell align="right">{T('Doc Δ', 'فرق السند')}</TableCell>
                      <TableCell>{T('JE #', 'القيد')}</TableCell>
                      <TableCell align="right">{T('JE Δ', 'فرق القيد')}</TableCell>
                      <TableCell align="center">{T('Match', 'تطابق')}</TableCell>
                      <TableCell align="center">{T('View', 'عرض')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.documents.map((d) => (
                      <TableRow key={`${d.documentType}-${d.documentId}`} hover sx={{
                        bgcolor: d.isMatching
                          ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4')
                          : (isDark ? 'rgba(239,68,68,0.10)' : '#fef2f2')
                      }}>
                        <TableCell>
                          <Button size="small" endIcon={<OpenIcon fontSize="small" />} onClick={() => openDoc(d.navigationPath)} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                            {d.documentNumber}
                          </Button>
                        </TableCell>
                        <TableCell><Chip size="small" label={isRTL ? d.documentTypeAr : d.documentType} variant="outlined" /></TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{new Date(d.documentDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(d.oldTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(d.newTotalCost, 3)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: d.documentDelta >= 0 ? '#10b981' : '#ef4444' }}>{d.documentDelta >= 0 ? '+' : ''}{fmt(d.documentDelta, 3)}</TableCell>
                        <TableCell>
                          {d.journalEntryNumber ? (
                            <Button size="small" endIcon={<OpenIcon fontSize="small" />} onClick={() => openDoc(d.journalNavigationPath)} sx={{ textTransform: 'none' }}>
                              {d.journalEntryNumber}
                            </Button>
                          ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: (d.jeDelta ?? 0) >= 0 ? '#10b981' : '#ef4444' }}>
                          {d.jeDelta != null ? `${d.jeDelta >= 0 ? '+' : ''}${fmt(d.jeDelta, 3)}` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {d.isMatching ? (
                            <Tooltip title={d.matchExplanation || ''}>
                              <Chip size="small" icon={<OkIcon />} label={T('✓ Match', '✓ مطابق')} color="success" sx={{ fontWeight: 'bold' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title={d.matchExplanation || ''}>
                              <Chip size="small" icon={<ErrorIcon />} label={T('✗ Mismatch', '✗ غير مطابق')} color="error" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {d.jeLines.length > 0 ? (
                            <Tooltip title={T('View JE lines before/after', 'عرض سطور القيد قبل/بعد')}>
                              <IconButton size="small" color="primary" onClick={() => setAuditJeOpen(d)}><JEIcon /></IconButton>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>{T('Close', 'إغلاق')}</Button>
        </DialogActions>
      </Dialog>

      {/* Location chooser dialog */}
      <Dialog open={locationOpen} onClose={() => setLocationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LifecycleIcon color="primary" />
            <span>{T('Select location for', 'اختر الموقع للصنف')} {locationItemCode}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {T('This item exists in multiple locations / units. Pick one to view its lifecycle (mixing locations gives meaningless balances).',
               'هذا الصنف موجود في عدة مواقع/وحدات. اختر موقعاً واحداً لعرض دورة حياته (مزج المواقع يعطي أرصدة بلا معنى).')}
          </Alert>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)', color: 'white', fontWeight: 'bold' } }}>
                  <TableCell>{T('Location', 'الموقع')}</TableCell>
                  <TableCell>{T('Unit', 'الوحدة')}</TableCell>
                  <TableCell align="right">{T('Current Qty', 'الرصيد')}</TableCell>
                  <TableCell align="right">{T('Current Value', 'القيمة')}</TableCell>
                  <TableCell align="right">{T('Movements', 'حركات')}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.locationKey} hover>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      {loc.warehouseId ? (isRTL ? (loc.warehouseNameAr || loc.warehouseName) : loc.warehouseName) : (isRTL ? (loc.branchNameAr || loc.branchName) : loc.branchName)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {loc.warehouseId ? T('Warehouse', 'مخزن') : T('Branch', 'فرع')}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={loc.unitCode} /></TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{fmt(loc.currentQuantity)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary' }}>{fmtKWD(loc.currentValue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary' }}>{loc.movementCount}</TableCell>
                    <TableCell align="center">
                      <Button variant="contained" size="small" endIcon={<NextIcon />}
                        onClick={() => locationItemId && loadLifecycleForLocation(locationItemId, locationItemCode, loc)}
                        sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
                        {T('Inspect', 'فحص')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationOpen(false)}>{T('Cancel', 'إلغاء')}</Button>
        </DialogActions>
      </Dialog>

      {/* Audit JE sub-dialog */}
      <Dialog open={!!auditJeOpen} onClose={() => setAuditJeOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <JEIcon color="primary" />
            <span>{T('Journal Entry Before/After', 'القيد قبل/بعد')}: {auditJeOpen?.journalEntryNumber}</span>
            <Chip size="small" label={auditJeOpen?.journalEntryStatus} color={auditJeOpen?.journalEntryStatus === 'Posted' ? 'success' : 'default'} />
          </Stack>
        </DialogTitle>
        <DialogContent>
          {auditJeOpen && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{T('Old Total Debit', 'إجمالي مدين قديم')}</Typography>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>{fmt(auditJeOpen.jeOldTotalDebit, 3)}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{T('New Total Debit', 'إجمالي مدين جديد')}</Typography>
                    <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 'bold' }}>{fmt(auditJeOpen.jeNewTotalDebit, 3)}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{T('Balanced?', 'متوازن؟')}</Typography>
                    <Typography variant="h6" sx={{ color: Math.abs((auditJeOpen.jeNewTotalDebit ?? 0) - (auditJeOpen.jeNewTotalCredit ?? 0)) < 0.01 ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                      {Math.abs((auditJeOpen.jeNewTotalDebit ?? 0) - (auditJeOpen.jeNewTotalCredit ?? 0)) < 0.01 ? T('✓ YES', '✓ نعم') : T('✗ NO', '✗ لا')}
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', fontWeight: 'bold' } }}>
                      <TableCell>#</TableCell>
                      <TableCell>{T('Account', 'الحساب')}</TableCell>
                      <TableCell align="right">{T('Old Debit', 'مدين قديم')}</TableCell>
                      <TableCell align="right">{T('New Debit', 'مدين جديد')}</TableCell>
                      <TableCell align="right">{T('Old Credit', 'دائن قديم')}</TableCell>
                      <TableCell align="right">{T('New Credit', 'دائن جديد')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditJeOpen.jeLines.map((ln) => (
                      <TableRow key={ln.entryDetailId} hover>
                        <TableCell sx={{ color: 'text.primary' }}>{ln.lineNumber}</TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight="bold" sx={{ color: 'text.primary' }}>{ln.accountCode}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{isRTL ? ln.accountNameAr : ln.accountName}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(ln.oldDebit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: ln.newDebit !== ln.oldDebit ? '#10b981' : 'text.primary', fontWeight: ln.newDebit !== ln.oldDebit ? 'bold' : 'normal' }}>{fmt(ln.newDebit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(ln.oldCredit, 3)}</TableCell>
                        <TableCell align="right" sx={{ color: ln.newCredit !== ln.oldCredit ? '#10b981' : 'text.primary', fontWeight: ln.newCredit !== ln.oldCredit ? 'bold' : 'normal' }}>{fmt(ln.newCredit, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditJeOpen(null)}>{T('Close', 'إغلاق')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryRebalancePage;
